import { Inject, Injectable } from "@angular/core";
import { isInlineTranslation } from "@smallstack/i18n-shared";
import { PageableCrudStore } from "@smallstack/store";
import { TextReplacer, Utils } from "@smallstack/utils";
import { BehaviorSubject } from "rxjs";
import { CURRENT_LOCALE$, FALLBACK_CHAINS } from "../services/locale.service";
import { IOC } from "@smallstack/core-common";
import * as i0 from "@angular/core";
import * as i1 from "rxjs";
export const TRANSLATION_KEY_REGEX = /(@@[a-zA-Z0-9._-]+)/gi;
export class TranslationStore extends PageableCrudStore {
    constructor(currentLocale$, fallbackChains) {
        super();
        this.currentLocale$ = currentLocale$;
        this.fallbackChains = fallbackChains;
        IOC.register("translator", this);
    }
    getByKey(key) {
        if (!key)
            return undefined;
        const valueFromStore = this.value?.find((t) => t.key.toLowerCase() === key);
        if (valueFromStore)
            return valueFromStore;
        if (this.fallbackStore)
            return this.fallbackStore.getByKey(key);
        return undefined;
    }
    translate(data, options = {}) {
        if (data === undefined)
            return;
        if (!options.locale)
            options.locale = this.currentLocale$.value;
        if (!options.locale)
            throw new Error("no currentLocale set in localeService and no locale given");
        if (typeof data === "string" && data.includes("@@")) {
            return data.replace(TRANSLATION_KEY_REGEX, (substr, args) => {
                return this.translateByKey(substr.substring(2), options);
            });
        }
        else if (isInlineTranslation(data))
            return this.translateInline(data, options);
        else
            return data;
    }
    translateByKey(key, options = {}) {
        const clearedKey = key.toLowerCase().replace(/[^a-z0-9._-]/gi, "");
        const translation = this.getByKey(clearedKey);
        if (!translation) {
            if (options.showMissingKey === true)
                return `_${options.locale}:${clearedKey}_`;
            else if (options.showMissingKey === null)
                return clearedKey;
            else
                return "";
        }
        if (!options.locale)
            options.locale = this.currentLocale$.value;
        if (!options.locale)
            throw new Error("no currentLocale set in localeService and no locale given");
        // translate textreplacers
        if (options.replacers)
            for (const key in options.replacers) {
                options.replacers[key] = this.translate(options.replacers[key]);
            }
        if (translation.values[options.locale])
            return TextReplacer.replace(translation.values[options.locale], options.replacers);
        // fallback chain
        if (options.showFallback !== false) {
            const fallbackChain = this.fallbackChains[options.locale];
            if (fallbackChain)
                for (const fallback of fallbackChain)
                    if (translation.values[fallback])
                        return TextReplacer.replace(translation.values[fallback], options.replacers);
        }
        // last way out, return first element
        if (Object.values(translation.values)[0])
            return TextReplacer.replace(Object.values(translation.values)[0], options.replacers);
        return key;
    }
    translateInline(inline, options = {}) {
        // inline translations coming from the api are mostly typeof string | InlineTranslation
        if (typeof inline === "string")
            return TextReplacer.replace(inline, options.replacers);
        if (!(inline instanceof Array))
            throw new Error("inline translation must be an array");
        let translation = inline.find((t) => t.locale === options.locale);
        if (translation)
            return TextReplacer.replace(translation.value, options.replacers);
        else {
            // locale fallback
            const fallbackLocales = this.fallbackChains[options.locale];
            if (fallbackLocales instanceof Array && fallbackLocales.length > 0) {
                for (const fallbackLocale of fallbackLocales) {
                    translation = inline.find((t) => t.locale === fallbackLocale);
                    if (translation)
                        return TextReplacer.replace(translation.value, options.replacers);
                }
            }
        }
        // last way out
        return TextReplacer.replace(inline[0]?.value, options.replacers);
    }
    addJsonTranslations(locales, translations) {
        const flattened = Utils.flattenJSON(translations, false);
        this.addValues(Object.keys(flattened).map((key) => {
            const values = {};
            for (const [index, locale] of locales.entries()) {
                values[locale] = flattened[key][index];
            }
            return { key: key.toLocaleLowerCase(), values };
        }));
    }
    addTranslationPack(locale, pack) {
        this.updateValue((translations) => {
            if (!translations)
                translations = [];
            for (const key in pack) {
                const translationIndex = translations.findIndex((translation) => translation.key === key);
                if (translationIndex !== -1)
                    translations[translationIndex].values[locale] = pack[key];
                else
                    translations.push({ key, values: { [locale]: pack[key] } });
            }
            return translations;
        });
    }
    loadModelById(id) {
        throw new Error("Please implement this method in case you need CRUD functionality in your current context!");
    }
    deleteModelById(id) {
        throw new Error("Please implement this method in case you need CRUD functionality in your current context!");
    }
    async deleteModelsByIds(ids) {
        throw new Error("Please implement this method in case you need CRUD functionality in your current context!");
    }
    createModel(model) {
        throw new Error("Please implement this method in case you need CRUD functionality in your current context!");
    }
    patchModel(id, model) {
        throw new Error("Please implement this method in case you need CRUD functionality in your current context!");
    }
    putModel(model) {
        throw new Error("Please implement this method in case you need CRUD functionality in your current context!");
    }
    loadModels(query) {
        throw new Error("Please implement this method in case you need CRUD functionality in your current context!");
    }
}
TranslationStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.5", ngImport: i0, type: TranslationStore, deps: [{ token: CURRENT_LOCALE$ }, { token: FALLBACK_CHAINS }], target: i0.ɵɵFactoryTarget.Injectable });
TranslationStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.5", ngImport: i0, type: TranslationStore, providedIn: "root" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.5", ngImport: i0, type: TranslationStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: "root" }]
        }], ctorParameters: function () { return [{ type: i1.BehaviorSubject, decorators: [{
                    type: Inject,
                    args: [CURRENT_LOCALE$]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FALLBACK_CHAINS]
                }] }]; } });
